// React hooks за state и memoization
import { useState, useMemo, useEffect, useRef } from "react";
import { lunchUnitFor } from "../../../lib/lunchCatalog";
import { useTranslation } from "react-i18next";
import { DisconnectOutlined, InboxOutlined } from "@ant-design/icons";
import { getItemName, getTranslation } from "../../../utils/translations";
// React Query hooks за меню данни и промяна на статус
import {
  useMenuItems,
  useMenuModifiers,
  useMenuStations,
} from "../../../hooks/useMenu";
import { useUpdateItemStatus } from "../../../hooks/useBills";
// Footer с бутони (В процес, Изпълни, Върни)
import { MonitorFooter } from "./MonitorFooter";
// View компоненти
import MonitorsGridView from "./MonitorsGridView";
import MonitorsListView from "./MonitorsListView";
// Модал с информация за продукт (преизползван от модул "Маркиране")
import { ItemDetailModal } from "../../ordering/components/menu/ItemDetailModal";
// WebSocket context за получаване на поръчки от всички станции
import { useStationWebSocketContext } from "./StationWebSocketProvider";
// Redux — права на текущия оператор (за gate на "Изпълни")
import { useAppSelector } from "../../../store/hooks";
// TypeScript типове
import type { Order, FlatProduct } from "../types";
import { WebSocketStatus } from "../types";
import { getRowId, orderIdentityKey } from "../types";
import {
  attachAdditions,
  completedQuantityKeyOf,
  partStationIdOf,
  productKeyOf,
  keepOnShpicker,
  otherStationParts,
  shpickerStationParts,
  tagFeedStation,
} from "../stationParts";
import type {
  MonitorColorBy,
  MonitorInfoStation,
  MonitorView,
} from "../../../api/monitors";

// Props за MonitorsContent
interface MonitorsContentProps {
  filterMode?: "unfulfilled" | "fulfilled";
  aggregatedQuantities?: boolean;
  completeFullQuantity?: boolean;
  view?: MonitorView;
  // Шпикер (expediter) монитор: „Изпълни" пише "3" (Издадено), табовете са
  // по шпикер правилата (виж unfulfilledStatuses/fulfilledStatuses по-долу).
  isShpicker?: boolean;
  // Само за шпикер: true → вижда и незапочнато/в процес, false → само сготвени.
  showAllStatuses?: boolean;
  // По какво се оцветяват редовете: маса (както досега), сервитьор, без.
  colorBy?: MonitorColorBy;
  // Разделен екран: панелът показва САМО тези станции (null/undefined → всички).
  paneStationIds?: number[] | null;
  // Разделен екран: само активният панел рендерира footer-а и слуша
  // клавиатурата — селекцията и стрелките не пресичат в другия панел.
  isActive?: boolean;
  // Клик където и да е в панела го прави активен.
  onActivate?: () => void;
  // Имената на станциите на монитора — за етикета на частта при продукт на
  // няколко станции (виж stationParts.ts).
  stationNames?: MonitorInfoStation[];
}

// Интерфейс за селектиран продукт
interface SelectedProduct {
  itemId: number;
  billId: number;
  billElementId: number;
  // Уникален ид на реда в бекенда — за прецизен статус ъпдейт, когато същият
  // продукт е на няколко отделни реда в една поръчка. null при стари payload-и.
  billElementItemId: number | null;
  isAddition: boolean;
  // Станцията на частта при продукт на няколко станции (обикновен монитор);
  // null → заявката е без `stationId` (ред с една станция или шпикер).
  stationId: number | null;
  productKey: string;
}

// Помощна функция — селекция от FlatProduct
const toSelectedProduct = (
  product: FlatProduct,
  productKey: string,
): SelectedProduct => ({
  itemId: product.itemId,
  billId: product.billId,
  billElementId: product.billElementId,
  billElementItemId: product.billElementItemId,
  isAddition: product.isAddition || false,
  stationId: product.stationId,
  productKey,
});

// Тялото на PATCH /bills/items/status за селектиран продукт. `stationId` се
// праща САМО за част от продукт на няколко станции — заявката за ред с една
// станция остава байт за байт досегашната.
const statusRequestOf = (
  product: SelectedProduct,
  status: "0" | "1" | "2" | "3",
) => ({
  itemId: product.itemId,
  billId: product.billId,
  billElementId: product.billElementId,
  billElementItemId: product.billElementItemId,
  isAddition: product.isAddition,
  status,
  ...(product.stationId != null ? { stationId: product.stationId } : {}),
});

const MonitorsContent = ({
  filterMode = "unfulfilled",
  aggregatedQuantities = false,
  completeFullQuantity = false,
  view = "grid",
  isShpicker = false,
  showAllStatuses = false,
  colorBy = "table",
  paneStationIds = null,
  isActive = true,
  onActivate,
  stationNames = [],
}: MonitorsContentProps) => {
  const { t, i18n } = useTranslation();
  // Права за трите действия във footer-а — без съответното право бутонът е недостъпен
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canMarkReady = permissions.some((p) => p.name === "mark_ready");
  const canMarkInProgress = permissions.some(
    (p) => p.name === "mark_in_progress",
  );
  const canReturnToUnprepared = permissions.some(
    (p) => p.name === "return_to_unprepared",
  );

  // Кои кухненски статуси влизат в двата таба на ТОЗИ екран:
  //  * обикновен монитор: Неизпълнени = 0/1, Изпълнени = 2 И 3 — издаденият от
  //    шпикера ред остава видим (синя иконка), но е НЕСЕЛЕКТИРУЕМ (шпикерът го
  //    владее; „Изпълни"/„Върни" тук биха му сменили статуса погрешно);
  //  * шпикер: Неизпълнени = 2 (или 0/1/2 при showAllStatuses), Изпълнени = 3.
  // „Изпълни" на шпикера пише "3" (Издадено), „Върни" връща на "2".
  const unfulfilledStatuses = isShpicker
    ? showAllStatuses
      ? ["0", "1", "2"]
      : ["2"]
    : ["0", "1"];
  const fulfilledStatuses = isShpicker ? ["3"] : ["2", "3"];
  const inertStatuses = isShpicker ? [] : ["3"];
  const completeTargetStatus: "2" | "3" = isShpicker ? "3" : "2";
  const returnTargetStatus: "0" | "2" = isShpicker ? "2" : "0";
  // Ред, по който може да се навигира/действа на ТОЗИ екран.
  const isNavigable = (product: FlatProduct) =>
    !product.isSplit &&
    !product.isCancelled &&
    !inertStatuses.includes(product.status);
  // Шпикерът издава целия ред наведнъж — инкременталното броене по бройки
  // няма смисъл на екрана, който само предава готовото към сервитьора.
  const effectiveCompleteFullQuantity = completeFullQuantity || isShpicker;
  // Получаваме всички станции с техните поръчки от WebSocket; при разделен
  // екран панелът гледа само своята половина от станциите.
  const { stations: allStations } = useStationWebSocketContext();
  const stations = useMemo(
    () =>
      paneStationIds
        ? allStations.filter((station) =>
            paneStationIds.includes(station.stationId),
          )
        : allStations,
    [allStations, paneStationIds],
  );
  // §5.3: празна дъска върху мъртъв сокет изглежда като спокойна кухня.
  // Затова състоянието на канала се чете ТУК — досега се смяташе в hook-а и
  // пътуваше през провайдъра, без нито един консуматор.
  const channelDown =
    stations.length > 0 &&
    stations.every((station) => station.status !== WebSocketStatus.CONNECTED);
  // React Query hooks за меню данни и промяна на статус
  const menuItems = useMenuItems();
  const menuModifiers = useMenuModifiers();
  // Имената на станциите за подтекста „+ Студена кухня" — мониторът знае
  // само своите (stationNames), другите идват от менюто.
  const menuStations = useMenuStations();
  const updateItemStatusMutation = useUpdateItemStatus();

  // State за селектирани продукти
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );

  // Маркира се при първото ръчно действие на потребителя (клик / стрелки / footer
  // навигация). Докато е false, в list view държим селекцията върху първия ред дори
  // когато flatProducts се пренарежда (например при инкрементално зареждане от WS).
  const userInteractedRef = useRef(false);

  // Изрично деселектиране (клик встрани / ↑ от най-горния ред): докато е
  // вдигнат, автоселекцията на първия ред в списъчния изглед не се връща.
  const explicitlyDeselectedRef = useRef(false);

  // State за проследяване на изпълнените бройки за всеки продукт
  // Key: itemId-billId-billElementId, Value: completedQuantity
  const [completedQuantities, setCompletedQuantities] = useState<
    Record<string, number>
  >({});

  // State за текущо време - обновява се на всяка минута за изчисляване на изминало време
  const [currentTime, setCurrentTime] = useState(new Date());

  // State за info модала: продуктът, който показваме
  const [infoItemId, setInfoItemId] = useState<number | null>(null);

  // Обновяваме текущото време на всяка минута за изчисляване на изминало време
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Обновяваме на всяка минута

    return () => clearInterval(interval);
  }, []);

  // Помощна функция за форматиране на ISO дата като HH:MM в локалната часова зона
  const formatTimeFromISO = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    } catch {
      return isoString;
    }
  };

  // Функция за изчисляване на изминало време от създаване на поръчката
  const calculateElapsedTime = (orderTime: string): string => {
    try {
      // Парсваме ISO датата (напр. "2026-03-31T12:06:39.764739+03:00")
      const orderDate = new Date(orderTime);

      // Изчисляваме разликата в минути (работи правилно за всички часови зони)
      const diffMs = currentTime.getTime() - orderDate.getTime();
      const totalMinutes = Math.max(0, Math.floor(diffMs / 60000));

      // Форматираме изхода
      if (totalMinutes < 60) {
        return `${totalMinutes}${t("monitors.minuteShort")}`;
      } else {
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return m > 0
          ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
          : `${h}${t("monitors.hourShort")}`;
      }
    } catch {
      return "";
    }
  };

  // СТЪПКА 1: Обединяване на поръчки от всички станции
  // Един и същ `bill_element` отива на няколко станции (кухня, бар) със СЪЩАТА
  // самоличност (billId, billElementId) — тук двете половини стават една
  // картичка. Ключът е самоличността, НЕ „маса-час": маса и час са съвпадение,
  // което слива чужди поръчки и събира количествата им (виж `orderIdentityKey`).
  const mergedOrders = useMemo(() => {
    // Map за групиране на поръчки по самоличност
    const orderMap = new Map<string, Order>();
    // Всички станции на ЕКРАНА (и двата панела) — шпикерът показва ред на
    // няколко станции само в канала на една от тях (keepOnShpicker).
    const listenedStationIds = allStations.map((station) => station.stationId);

    // Обхождаме всички станции
    stations.forEach((station) => {
      // Обхождаме всички поръчки в станцията
      station.orders.forEach((order) => {
        const key = orderIdentityKey(order);
        // Всеки ред помни от канала на коя станция е дошъл — продукт на
        // няколко станции е отделна част за всяка (виж stationParts.ts).
        const tagged = tagFeedStation(
          order.elements,
          station.stationId,
          isShpicker,
        );
        const elements = isShpicker
          ? tagged.filter((element) =>
              keepOnShpicker(element, station.stationId, listenedStationIds),
            )
          : tagged;

        // Ако вече имаме поръчка с този ключ, добавяме продуктите към нея
        if (orderMap.has(key)) {
          const existingOrder = orderMap.get(key)!;
          existingOrder.elements = [...existingOrder.elements, ...elements];
        } else {
          // Ако е нова поръчка, я добавяме в Map-а
          orderMap.set(key, { ...order, elements });
        }
      });
    });

    // Филтриране според избрания режим - на ниво продукт
    // Включваме поръчки, които имат поне 1 продукт, отговарящ на филтъра
    const allOrders = Array.from(orderMap.values());
    const filteredOrders = allOrders.filter((order) => {
      // Филтрираме само активните продукти (без split и cancelled)
      const activeItems = order.elements.filter(
        (item) => !item.isSplit && !item.isCancelled,
      );

      // Проверяваме дали има cancelled продукти (без split)
      const hasCancelled = order.elements.some(
        (item) => item.isCancelled && !item.isSplit,
      );

      // Проверяваме дали има продукти за таб „Неизпълнени" на този екран
      const hasUnfulfilled = activeItems.some((item) =>
        unfulfilledStatuses.includes(item.status),
      );

      // Проверяваме дали има продукти за таб „Изпълнени" на този екран
      const hasFulfilled = activeItems.some((item) =>
        fulfilledStatuses.includes(item.status),
      );

      // Ако няма активни продукти (само split/cancelled)
      if (activeItems.length === 0) {
        // Поръчка само с cancelled → показваме в "fulfilled" (считаме за приключена)
        if (hasCancelled) return filterMode === "fulfilled";
        return filterMode === "fulfilled";
      }

      if (filterMode === "fulfilled") {
        // Поне 1 завършен продукт ИЛИ cancelled без незавършени активни
        return hasFulfilled || (hasCancelled && !hasUnfulfilled);
      } else {
        return hasUnfulfilled;
      }
    });

    // Функция за изчисляване на изминало време в минути от ISO дата
    const getElapsedMinutes = (orderTime: string): number => {
      try {
        const orderDate = new Date(orderTime);
        const diffMs = currentTime.getTime() - orderDate.getTime();
        return Math.max(0, Math.floor(diffMs / 60000));
      } catch {
        return 0;
      }
    };

    // Сортиране по реално изминало време в зависимост от режима, след това по tableName
    return filteredOrders.sort((a, b) => {
      // Изчисляваме реалното изминало време за всяка поръчка
      const aElapsed = getElapsedMinutes(a.time);
      const bElapsed = getElapsedMinutes(b.time);

      // Първо сортираме по изминало време
      const timeComparison =
        filterMode === "fulfilled"
          ? aElapsed - bElapsed // Изпълнени - най-новите първи (по-малко изминало време)
          : bElapsed - aElapsed; // Неизпълнени - най-старите първи (повече изминало време)

      // Ако времената са еднакви, сортираме по tableName
      if (timeComparison === 0) {
        // Проверяваме дали tableName са числа
        const aNum = Number(a.tableName);
        const bNum = Number(b.tableName);

        // Ако и двете са валидни числа, сравняваме числово
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }

        // Иначе сравняваме като текст (localeCompare поддържа естествено сортиране)
        return a.tableName.localeCompare(b.tableName, "bg", {
          numeric: true,
          sensitivity: "base",
        });
      }

      return timeComparison;
    });
    // unfulfilledStatuses/fulfilledStatuses са изведени от двата пропа по-долу
  }, [stations, allStations, filterMode, currentTime, isShpicker, showAllStatuses]);

  // СТЪПКА 2: Преобразуване на поръчки в flat list от продукти
  // Вместо групирани поръчки, получаваме един масив от всички продукти
  // Пример: Order1[prod1, prod2], Order2[prod3] → [prod1, prod2, prod3]
  const flatProducts = useMemo(() => {
    const products: FlatProduct[] = [];

    // Обхождаме всички обединени поръчки
    mergedOrders.forEach((order) => {
      const orderKey = orderIdentityKey(order);

      // СТЪПКА 2.1: Групиране на еднакви продукти
      // Всеки ред от бекенда (bill_element_item / addition) е ОТДЕЛЕН ред на
      // монитора — ключът включва уникалния ид на реда (getRowId), затова редове
      // със същия продукт НЕ се сливат (всяко отделно добавяне при маркиране си
      // остава отделен ред). Групирането остава само за дедупликация на един и
      // същ физически ред, дошъл повторно (напр. при merge на станции), и като
      // fallback за стари payload-и без ид (rowId === null). Част от продукт
      // на няколко станции носи и станцията си (productKeyOf) — двете части
      // са различни редове на екрана, не повторна доставка.
      const getProductKey = (item: (typeof order.elements)[0]): string =>
        productKeyOf(item, isShpicker);

      // Групираме еднакви продукти
      const groupedElements = order.elements.reduce(
        (acc, item) => {
          const key = getProductKey(item);
          const existing = acc.find((el) => getProductKey(el) === key);

          if (!existing) {
            // Добавяме нов продукт
            acc.push({ ...item });
            return acc;
          }

          // Ред с ид, срещнат втори път, е ЕДИН И СЪЩ физически ред, дошъл
          // повторно (дублиран кадър, merge на станции, пакетът на сторното) —
          // количеството е на реда, не е сбор от доставките. Събирането правеше
          // „2 × Бургер" на „4 × Бургер" (докладвано на живо 22.09.2026).
          // Първото срещане е и най-свежото: новите кадри влизат в началото на
          // списъка на станцията.
          // Сборът остава САМО за стари payload-и без ид на реда (rowId ===
          // null) — там наистина няма с какво да различим повторение от втори
          // ред със същия продукт, а да загубим бройка е по-скъпо.
          if (getRowId(item) == null) {
            existing.quantity += item.quantity;
          }

          return acc;
        },
        [] as typeof order.elements,
      );

      // СТЪПКА 2.2: Разделяме продуктите на основни и добавки
      // Основните продукти се сортират по mainBillElementItemId — id-тата се
      // раздават в реда на маркиране (BE flush per продукт), а merge-ът на
      // станциите ги слепва станция по станция. Без сорта поръчка с продукти
      // на 2+ станции (напр. рецепти на кухня + бар) излиза групирана по
      // станция вместо в реда на накликване. Редове без id (стари payload-и)
      // пазят относителния си ред (stable sort).
      const mainProducts = groupedElements
        .filter((item) => !item.isAddition)
        .sort((a, b) => {
          // Ред без id (стар payload) отива СЛЕД редовете с id — иначе
          // сравнението е противоречиво и сортът разбърква при смесени данни
          if (a.mainBillElementItemId != null && b.mainBillElementItemId != null)
            return a.mainBillElementItemId - b.mainBillElementItemId;
          if (a.mainBillElementItemId != null) return -1;
          if (b.mainBillElementItemId != null) return 1;
          return 0;
        });
      const additionProducts = groupedElements.filter(
        (item) => item.isAddition,
      );

      // СТЪПКА 2.3–2.4: Всяка добавка — непосредствено под родителя си
      // (контекстът „към {продукт}" е излишен и се чисти), добавките без
      // видим родител — накрая. Правилата са в attachAdditions.
      const reorderedElements = attachAdditions(mainProducts, additionProducts);
      // Етикет на частта — само когато панелът гледа повече от една станция
      // (иначе е ясно чия е частта).
      const showPartTag = stations.length > 1;
      const paneStationIdList = stations.map((station) => station.stationId);
      const stationNameOf = (stationId: number): string | null => {
        const own = stationNames.find((station) => station.id === stationId);
        if (own) return own.name;
        const fromMenu = menuStations.find((station) => station.id === stationId);
        return fromMenu
          ? getTranslation(fromMenu.translations, i18n.language, fromMenu.slug) ||
              null
          : null;
      };
      // Подтекстът за другите станции на реда (виж stationParts.ts).
      const stationHintOf = (item: (typeof order.elements)[0]): string | null => {
        const parts = isShpicker
          ? showAllStatuses
            ? shpickerStationParts(item)
            : []
          : otherStationParts(item, paneStationIdList);
        const named = parts
          .map((part) => ({ ...part, name: stationNameOf(part.stationId) }))
          .filter((part) => part.name);
        if (named.length === 0) return null;
        if (isShpicker) {
          return named
            .map((part) => `${part.name} ${part.state === "done" ? "✓" : "…"}`)
            .join(" · ");
        }
        const stateLabel = {
          done: t("monitors.stationPartDone"),
          inProgress: t("monitors.stationPartInProgress"),
          waiting: t("monitors.stationPartWaiting"),
          untracked: "",
        };
        return `+ ${named
          .map((part) =>
            part.state === "untracked"
              ? part.name
              : `${part.name} · ${stateLabel[part.state]}`,
          )
          .join(", ")}`;
      };

      // СТЪПКА 2.3: Филтриране на ниво продукт според filterMode
      // В "fulfilled" показваме само готови (статус 2), в "unfulfilled" - само незавършени
      const filteredElements = reorderedElements.filter((item) => {
        // Split продукти не показваме в нито един таб
        if (item.isSplit) return false;

        // Cancelled продукти показваме и в двата таба (сиви и задраскани)
        if (item.isCancelled) return true;

        if (filterMode === "fulfilled") {
          return fulfilledStatuses.includes(item.status);
        } else {
          return unfulfilledStatuses.includes(item.status);
        }
      });

      // Изминало време в минути от поръчката (за overdue проверка)
      const orderElapsedMinutes = (() => {
        try {
          const orderDate = new Date(order.time);
          const diffMs = currentTime.getTime() - orderDate.getTime();
          return Math.max(0, Math.floor(diffMs / 60000));
        } catch {
          return 0;
        }
      })();

      // СТЪПКА 2.4: Преобразуваме филтрираните елементи във FlatProduct формат
      filteredElements.forEach((item, index) => {
        const rowId = getRowId(item);
        const partStationId = partStationIdOf(item, isShpicker);
        const itemKey = completedQuantityKeyOf({
          itemId: item.itemId,
          billId: order.billId,
          billElementId: order.billElementId,
          billElementItemId: rowId,
          stationId: partStationId,
        });
        const completedQty = completedQuantities[itemKey] || 0;
        const partStationName =
          partStationId != null && showPartTag
            ? (stationNames.find((station) => station.id === partStationId)
                ?.name ?? null)
            : null;
        const menuItem = menuItems.find((mi) => mi.id === item.itemId);

        // Unit/size: обеденият (запомнен при поръчката, на текущия език, иначе
        // на български) — иначе от itemLang за текущия език (fallback към първи наличен)
        const lunchUnitAndSize = lunchUnitFor(item.lunchUnitLangs, i18n.language);
        const unitAndSize =
          lunchUnitAndSize ??
          (menuItem
            ? (menuItem.itemLangs.find(
                (lang) => lang.languageCode === i18n.language,
              )?.unitAndSize ??
              menuItem.itemLangs[0]?.unitAndSize ??
              null)
            : null);

        products.push({
          orderKey,
          tableName: order.tableName,
          oldTableName: order.oldTableName,
          waiterName: order.waiterName,
          time: order.time,
          comment: order.comment,
          productName: menuItem
            ? getItemName(menuItem.itemLangs, i18n.language, item.name)
            : item.name,
          quantity: item.quantity,
          status: item.status,
          isSplit: item.isSplit || false,
          isCancelled: item.isCancelled || false,
          isFirstInOrder: index === 0, // Първият филтриран продукт показва header на поръчката
          productIndex: index,
          itemId: item.itemId,
          billId: order.billId,
          billElementId: order.billElementId,
          billElementItemId: rowId,
          completedQuantity: completedQty,
          modifiers: item.modifiers?.map((mod) => {
            const menuMod = menuModifiers.find(
              (mm) => mm.id === Number(mod.id),
            );
            return {
              id: mod.id,
              name: menuMod
                ? getTranslation(menuMod.translations, i18n.language, mod.name)
                : mod.name,
            };
          }),
          hasAdditions: item.hasAdditions,
          isAddition: item.isAddition,
          mainProductName: item.mainProductName ?? null,
          mainStationName: item.mainStationName ?? null,
          stationId: partStationId,
          stationTag: partStationName,
          stationHint: stationHintOf(item),
          unitAndSize,
          isLunch: item.lunchMenuId != null,
          lunchUnitAndSize,
          prepareTime: item.prepareTime ?? null,
          isOverdue:
            item.prepareTime != null &&
            orderElapsedMinutes > item.prepareTime,
        });
      });
    });

    return products;
  }, [
    mergedOrders,
    completedQuantities,
    filterMode,
    isShpicker,
    showAllStatuses,
    i18n.language,
    menuItems,
    menuModifiers,
    stations,
    stationNames,
    menuStations,
    t,
  ]);

  // Общ брой по itemId за всички видими продукти (за aggregatedQuantities).
  // Частите на продукт на няколко станции са ЕДНА порция — редът се брои
  // веднъж, колкото и части да се виждат.
  const aggregatedTotals = useMemo(() => {
    if (!aggregatedQuantities) return {};
    const totals: Record<number, number> = {};
    const countedParts = new Set<string>();
    flatProducts.forEach((p) => {
      if (p.isCancelled || p.isSplit) return;
      if (p.stationId != null) {
        const rowKey = `${p.orderKey}-${p.isAddition}-${p.billElementItemId}`;
        if (countedParts.has(rowKey)) return;
        countedParts.add(rowKey);
      }
      totals[p.itemId] = (totals[p.itemId] || 0) + p.quantity;
    });
    return totals;
  }, [flatProducts, aggregatedQuantities]);

  // В list view винаги има по дефаулт селектиран продукт — първия навигируем.
  // Така footer бутоните и +/- shortcut-ите работят без предварителен клик.
  // Докато потребителят не е извършил ръчно действие, поддържаме селекцията върху
  // текущия първи ред — иначе при инкрементално зареждане от WS селекцията може
  // да остане на ред, който след пренареждане вече не е първи.
  useEffect(() => {
    if (view !== "list" || !isActive) return;
    const firstNavigable = flatProducts.find(isNavigable);
    if (!firstNavigable) return;
    const firstKey = `${firstNavigable.orderKey}-${firstNavigable.productIndex}`;
    if (selectedProducts.length === 0) {
      if (explicitlyDeselectedRef.current) return;
      setSelectedProducts([toSelectedProduct(firstNavigable, firstKey)]);
      return;
    }
    if (
      !userInteractedRef.current &&
      (selectedProducts.length !== 1 ||
        selectedProducts[0].productKey !== firstKey)
    ) {
      setSelectedProducts([toSelectedProduct(firstNavigable, firstKey)]);
    }
  }, [view, flatProducts, selectedProducts, isActive]);

  // Разделен екран: панел, който губи активност, сваля селекцията си —
  // на екрана има точно една селекция и тя е в активния панел.
  useEffect(() => {
    if (isActive) return;
    setSelectedProducts((prev) => (prev.length > 0 ? [] : prev));
  }, [isActive]);

  // Keyboard shortcuts: "+" → Изпълни, "-" → Върни
  // Работят само върху селектирани продукти и се игнорират при focus в input/textarea
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // При разделен екран клавиатурата действа само в активния панел —
      // стрелките не могат да пресекат в другата половина.
      if (!isActive) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const isPlus =
        e.key === "+" ||
        e.key === "=" ||
        e.code === "NumpadAdd" ||
        e.code === "Equal";
      const isMinus =
        e.key === "-" ||
        e.code === "NumpadSubtract" ||
        e.code === "Minus";
      const isUp = e.key === "ArrowUp";
      const isDown = e.key === "ArrowDown";

      if (isUp || isDown) {
        const hasNavigable = flatProducts.some(isNavigable);
        if (!hasNavigable) return;
        e.preventDefault();
        const magnitude = e.shiftKey ? 2 : 1;
        handleNavigate(isUp ? -magnitude : magnitude);
        return;
      }

      if (!isPlus && !isMinus) return;
      if (selectedProducts.length === 0) return;

      e.preventDefault();
      if (isPlus) {
        if (canMarkReady) handleComplete();
      } else if (canReturnToUnprepared) handleReturn();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProducts,
    flatProducts,
    completedQuantities,
    canMarkReady,
    canReturnToUnprepared,
    isActive,
  ]);

  // Handler за навигация през продуктите със стрелките в footer-а
  // step: -2 / -1 / +1 / +2 — премества селекцията нагоре/надолу през N селектируеми продукта
  const handleNavigate = (step: number) => {
    userInteractedRef.current = true;
    const navigable = flatProducts.filter(isNavigable);
    if (navigable.length === 0) return;

    const selectionKeyOf = (p: FlatProduct) =>
      `${p.orderKey}-${p.productIndex}`;

    // Текущият "курсор" - последно селектирания продукт в списъка
    let currentIndex = -1;
    if (selectedProducts.length > 0) {
      const selectedKeys = new Set(selectedProducts.map((p) => p.productKey));
      for (let i = navigable.length - 1; i >= 0; i--) {
        if (selectedKeys.has(selectionKeyOf(navigable[i]))) {
          currentIndex = i;
          break;
        }
      }
    }

    // ↑ от най-горния селектиран ред → сваляме селекцията изцяло
    if (currentIndex === 0 && step < 0) {
      explicitlyDeselectedRef.current = true;
      setSelectedProducts([]);
      return;
    }

    const nextIndex =
      currentIndex === -1
        ? step > 0
          ? 0
          : navigable.length - 1
        : Math.max(0, Math.min(navigable.length - 1, currentIndex + step));

    const target = navigable[nextIndex];
    if (!target) return;

    setSelectedProducts([toSelectedProduct(target, selectionKeyOf(target))]);
  };

  // Handler за селектиране на продукт
  const handleProductClick = (product: FlatProduct) => {
    onActivate?.();
    // Не позволяваме селекция на split/cancelled/инертни („Издадено" при
    // обикновен монитор) редове
    if (!isNavigable(product)) {
      return;
    }
    userInteractedRef.current = true;
    explicitlyDeselectedRef.current = false;

    const productKey = `${product.orderKey}-${product.productIndex}`;

    setSelectedProducts((prev) => {
      const isAlreadySelected = prev.some((p) => p.productKey === productKey);

      if (isAlreadySelected) {
        // Ако е селектиран, го премахваме
        return prev.filter((p) => p.productKey !== productKey);
      } else {
        // Ако не е селектиран, го добавяме
        return [...prev, toSelectedProduct(product, productKey)];
      }
    });
  };

  // Handler за селектиране на цяла поръчка чрез header
  const handleOrderHeaderClick = (orderKey: string) => {
    onActivate?.();
    userInteractedRef.current = true;
    explicitlyDeselectedRef.current = false;
    const orderProducts = flatProducts.filter(
      (product) => product.orderKey === orderKey && isNavigable(product),
    );

    if (orderProducts.length === 0) return;

    setSelectedProducts((prev) => {
      const orderProductKeys = new Set(
        orderProducts.map(
          (product) => `${product.orderKey}-${product.productIndex}`,
        ),
      );
      const selectedProductKeys = new Set(prev.map((p) => p.productKey));
      const isOrderFullySelected = orderProducts.every((product) =>
        selectedProductKeys.has(`${product.orderKey}-${product.productIndex}`),
      );

      if (isOrderFullySelected) {
        return prev.filter(
          (product) => !orderProductKeys.has(product.productKey),
        );
      }

      const newSelections = orderProducts
        .filter(
          (product) =>
            !selectedProductKeys.has(
              `${product.orderKey}-${product.productIndex}`,
            ),
        )
        .map((product) =>
          toSelectedProduct(
            product,
            `${product.orderKey}-${product.productIndex}`,
          ),
        );

      return [...prev, ...newSelections];
    });
  };

  // Клик встрани от редовете (фонът на изгледа) сваля селекцията.
  const handleBackgroundClick = () => {
    onActivate?.();
    if (selectedProducts.length === 0) return;
    userInteractedRef.current = true;
    explicitlyDeselectedRef.current = true;
    setSelectedProducts([]);
  };

  // Handler за бутон "Инфо" — показва модал с информация за селектирания продукт.
  // Активен е само когато е селектиран точно един продукт.
  const handleInfo = () => {
    if (selectedProducts.length !== 1) return;
    setInfoItemId(selectedProducts[0].itemId);
  };

  const infoItem = useMemo(
    () =>
      infoItemId != null
        ? (menuItems.find((mi) => mi.id === infoItemId) ?? null)
        : null,
    [infoItemId, menuItems],
  );

  // Handler за бутон "В процес" - статус "1"
  const handleInProgress = async () => {
    if (selectedProducts.length === 0) return;

    // Изпращаме заявки за всички селектирани продукти
    for (const product of selectedProducts) {
      await updateItemStatusMutation.mutateAsync(
          statusRequestOf(product, "1"),
        );
    }

    // НАРОЧНО: действието изконсумира селекцията и се започва на чисто
    // (потвърдено продуктово решение, 19.08.2026) — не е пропуск.
    setSelectedProducts([]);
  };

  // Handler за бутон "Изпълни" - инкрементално изпълнение за количество > 1
  const handleComplete = async () => {
    if (selectedProducts.length === 0) return;

    // Запомняме позицията на последно селектирания продукт в navigable списъка,
    // за да може при пълно изпълнение селекцията да слезе на следващия ред.
    const navigable = flatProducts.filter(isNavigable);
    const selectionKeyOf = (p: FlatProduct) =>
      `${p.orderKey}-${p.productIndex}`;
    const selectedKeys = new Set(
      selectedProducts.map((p) => p.productKey),
    );
    let anchorIndex = -1;
    for (let i = navigable.length - 1; i >= 0; i--) {
      if (selectedKeys.has(selectionKeyOf(navigable[i]))) {
        anchorIndex = i;
        break;
      }
    }
    const completedKeys = new Set<string>();

    // Изпращаме заявки за всички селектирани продукти
    for (const product of selectedProducts) {
      // Намираме продукта в flatProducts за да вземем неговото количество
      const productKey = product.productKey;
      const flatProduct = flatProducts.find(
        (p) => `${p.orderKey}-${p.productIndex}` === productKey,
      );

      if (!flatProduct) continue;

      const itemKey = completedQuantityKeyOf(product);

      // Ако количеството е 1, или мониторът е в режим "цяло количество"
      // (шпикерът винаги издава целия ред) — директно крайният статус
      if (flatProduct.quantity === 1 || effectiveCompleteFullQuantity) {
        await updateItemStatusMutation.mutateAsync(
          statusRequestOf(product, completeTargetStatus),
        );
        // При режим "цяло количество" изчистваме locally-tracked progress
        if (effectiveCompleteFullQuantity) {
          setCompletedQuantities((prev) => {
            if (!(itemKey in prev)) return prev;
            const next = { ...prev };
            delete next[itemKey];
            return next;
          });
        }
        completedKeys.add(productKey);
      } else {
        // Ако количеството е > 1, увеличаваме completedQuantity
        const newCompletedQuantity = flatProduct.completedQuantity + 1;

        // Актуализираме локалното state
        setCompletedQuantities((prev) => ({
          ...prev,
          [itemKey]: newCompletedQuantity,
        }));

        // Ако сме изпълнили всички бройки, сменяме статуса на крайния
        if (newCompletedQuantity >= flatProduct.quantity) {
          await updateItemStatusMutation.mutateAsync(
          statusRequestOf(product, completeTargetStatus),
        );
          // Изчистваме completedQuantity след пълно изпълнение
          setCompletedQuantities((prev) => {
            const newState = { ...prev };
            delete newState[itemKey];
            return newState;
          });
          completedKeys.add(productKey);
        } else {
          // Иначе само сменяме статуса на "1" (в процес)
          await updateItemStatusMutation.mutateAsync(
          statusRequestOf(product, "1"),
        );
        }
      }
    }

    // След мутациите: ако всички селектирани са изпълнени, преместваме селекцията
    // на следващия navigable продукт под anchor позицията (или най-близкия наличен).
    if (completedKeys.size > 0) {
      setSelectedProducts((prev) => {
        const remaining = prev.filter(
          (p) => !completedKeys.has(p.productKey),
        );
        if (remaining.length > 0) return remaining;
        if (anchorIndex < 0) return [];
        // Търсим следващия продукт, който не е измежду току-що завършените
        let nextProduct: FlatProduct | undefined;
        for (let i = anchorIndex + 1; i < navigable.length; i++) {
          if (!completedKeys.has(selectionKeyOf(navigable[i]))) {
            nextProduct = navigable[i];
            break;
          }
        }
        // Fallback: предишния непопълнен (ако няма нищо надолу)
        if (!nextProduct) {
          for (let i = anchorIndex - 1; i >= 0; i--) {
            if (!completedKeys.has(selectionKeyOf(navigable[i]))) {
              nextProduct = navigable[i];
              break;
            }
          }
        }
        if (!nextProduct) return [];
        return [toSelectedProduct(nextProduct, selectionKeyOf(nextProduct))];
      });
    }
  };

  // Handler за бутон "Върни" - декрементално връщане за количество > 1
  const handleReturn = async () => {
    if (selectedProducts.length === 0) return;

    // Изпращаме заявки за всички селектирани продукти
    for (const product of selectedProducts) {
      // Намираме продукта в flatProducts за да вземем неговото количество
      const productKey = product.productKey;
      const flatProduct = flatProducts.find(
        (p) => `${p.orderKey}-${p.productIndex}` === productKey,
      );

      if (!flatProduct) continue;

      const itemKey = completedQuantityKeyOf(product);

      // Ако количеството е 1, completedQuantity е 0, или мониторът е в режим
      // "цяло количество" — директно връщаме (шпикер: "3" → "2"; иначе → "0")
      if (
        flatProduct.quantity === 1 ||
        flatProduct.completedQuantity === 0 ||
        effectiveCompleteFullQuantity
      ) {
        await updateItemStatusMutation.mutateAsync(
          statusRequestOf(product, returnTargetStatus),
        );
        // Изчистваме completedQuantity
        setCompletedQuantities((prev) => {
          const newState = { ...prev };
          delete newState[itemKey];
          return newState;
        });
        // Премахваме селекцията на този продукт след пълно връщане
        setSelectedProducts((prev) =>
          prev.filter((p) => p.productKey !== productKey),
        );
      } else {
        // Ако количеството е > 1 и има изпълнени бройки, намаляваме completedQuantity
        const newCompletedQuantity = flatProduct.completedQuantity - 1;

        // Актуализираме локалното state
        setCompletedQuantities((prev) => ({
          ...prev,
          [itemKey]: newCompletedQuantity,
        }));

        // Ако completedQuantity стане 0, връщаме статуса в началото на оста
        if (newCompletedQuantity <= 0) {
          await updateItemStatusMutation.mutateAsync(
          statusRequestOf(product, returnTargetStatus),
        );
          // Изчистваме completedQuantity
          setCompletedQuantities((prev) => {
            const newState = { ...prev };
            delete newState[itemKey];
            return newState;
          });
          // Премахваме селекцията на този продукт след пълно връщане
          setSelectedProducts((prev) =>
            prev.filter((p) => p.productKey !== productKey),
          );
        } else {
          // Иначе оставяме статуса на "1" (в процес)
          await updateItemStatusMutation.mutateAsync(
          statusRequestOf(product, "1"),
        );
        }
      }
    }
  };

  return (
    <>
      {/* Основен контейнер за продуктите */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Ако няма продукти, показваме празно съобщение */}
        {flatProducts.length === 0 ? (
          <div className="flex h-full items-center justify-center rounded-[14px] border border-shade-2 bg-surface">
            <div className="flex flex-col items-center gap-[10px] px-[24px] py-[32px] text-center">
              <div
                className={`flex h-[52px] w-[52px] items-center justify-center rounded-[14px] text-[22px] ${
                  channelDown
                    ? "bg-danger-tint text-danger-ink"
                    : "bg-brand-tint text-brand-deep-ink"
                }`}
              >
                {channelDown ? <DisconnectOutlined /> : <InboxOutlined />}
              </div>
              <div
                className={`text-[10.5px] font-bold uppercase tracking-[1.2px] ${
                  channelDown ? "text-danger-ink" : "text-muted"
                }`}
              >
                {channelDown
                  ? t("monitors.noConnection")
                  : t("monitors.noActiveOrders")}
              </div>
              <div className="max-w-[320px] text-[13px] leading-[1.45] text-body">
                {channelDown
                  ? t("monitors.noConnectionHint")
                  : t("monitors.newOrdersWillAppear")}
              </div>
            </div>
          </div>
        ) : view === "list" ? (
          <MonitorsListView
            flatProducts={flatProducts}
            selectedProducts={selectedProducts}
            aggregatedTotals={aggregatedTotals}
            aggregatedQuantities={aggregatedQuantities}
            formatTimeFromISO={formatTimeFromISO}
            calculateElapsedTime={calculateElapsedTime}
            onProductClick={handleProductClick}
            onBackgroundClick={handleBackgroundClick}
            colorBy={colorBy}
            fulfilledStatuses={fulfilledStatuses}
          />
        ) : (
          <MonitorsGridView
            flatProducts={flatProducts}
            selectedProducts={selectedProducts}
            aggregatedTotals={aggregatedTotals}
            formatTimeFromISO={formatTimeFromISO}
            calculateElapsedTime={calculateElapsedTime}
            onProductClick={handleProductClick}
            onOrderHeaderClick={handleOrderHeaderClick}
            onBackgroundClick={handleBackgroundClick}
            colorBy={colorBy}
            fulfilledStatuses={fulfilledStatuses}
            showUnstartedIcon={isShpicker && showAllStatuses}
          />
        )}
      </div>

      {/* Footer с бутони - fixed позиция отдолу. При разделен екран той е
          ОБЩ: рендерира го само активният панел, затова бутоните действат
          върху селекцията на панела, в който последно е цъкнато. */}
      {isActive && (
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <MonitorFooter
          onInfo={handleInfo}
          onInProgress={handleInProgress}
          onComplete={handleComplete}
          onReturn={handleReturn}
          onNavigate={handleNavigate}
          canNavigate={flatProducts.some(isNavigable)}
          canInfo={selectedProducts.length === 1}
          canComplete={canMarkReady}
          // Шпикерът само издава готовото — „В процес" е кухненско действие
          canInProgress={canMarkInProgress && !isShpicker}
          canReturn={canReturnToUnprepared}
          selectedOrderKey={selectedProducts.length > 0 ? "selected" : null}
        />
      </div>
      )}

      <ItemDetailModal
        item={infoItem}
        open={infoItem !== null}
        onClose={() => setInfoItemId(null)}
      />
    </>
  );
};

export default MonitorsContent;
